import { useState, useEffect, useCallback, useRef } from 'react'
import { ApiError } from '../api/http'

export interface ResourceCfg<T> {
  list(): Promise<T[]>
  create?(v: Partial<T>): Promise<T>
  update?(id: string, v: Partial<T>): Promise<T>
  remove?(id: string): Promise<void>
}

export interface ResourceState<T> {
  items: T[]
  loading: boolean
  error: string | null
  reload(): Promise<void>
  create(v: Partial<T>): Promise<void>
  update(id: string, v: Partial<T>): Promise<void>
  remove(id: string): Promise<void>
}

function toMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  return 'Unknown error'
}

/**
 * Generic CRUD hook. Loads list() on mount; mutations call the cfg fn
 * then reload(). Catches ApiError and puts .message into error.
 * All state updates are immutable.
 */
export function useResource<T>(cfg: ResourceCfg<T>): ResourceState<T> {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Keep cfg in a ref so callbacks stay stable even when the caller
  // passes a new inline object each render.
  const cfgRef = useRef(cfg)
  cfgRef.current = cfg

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await cfgRef.current.list()
      setItems(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : toMessage(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const create = useCallback(
    async (v: Partial<T>): Promise<void> => {
      if (!cfgRef.current.create) return
      setError(null)
      try {
        await cfgRef.current.create(v)
        await reload()
      } catch (e) {
        setError(e instanceof ApiError ? e.message : toMessage(e))
      }
    },
    [reload]
  )

  const update = useCallback(
    async (id: string, v: Partial<T>): Promise<void> => {
      if (!cfgRef.current.update) return
      setError(null)
      try {
        await cfgRef.current.update(id, v)
        await reload()
      } catch (e) {
        setError(e instanceof ApiError ? e.message : toMessage(e))
      }
    },
    [reload]
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      if (!cfgRef.current.remove) return
      setError(null)
      try {
        await cfgRef.current.remove(id)
        await reload()
      } catch (e) {
        setError(e instanceof ApiError ? e.message : toMessage(e))
      }
    },
    [reload]
  )

  return { items, loading, error, reload, create, update, remove }
}
